import type { TadaDocumentNode } from 'gql.tada'
import { shake } from 'radashi'
import type { UnknownRecord } from 'type-fest'

import type { Username } from '@isoapp/common/platform/types/user/username'

import { auth, client } from '#@/platform/networking/client/lib/client'
import { config as globalConfig } from '#@/platform/networking/client/lib/config'
import { aapi, django, graph, mapi, shiphero } from '#@/platform/networking/client/lib/endpoints'
import type { Extension } from '#@/platform/networking/client/types/extension'
import type { MarketplaceGid } from '#@/platform/types/marketplace/marketplaceGid'

export type RequestFn = <T, V extends UnknownRecord>(document: TadaDocumentNode<T, V>, variables: V) => Promise<T>
type RequestConfig = {
  auth?: boolean
  django?: boolean
  mapi?: MarketplaceGid | null
  aapi?: MarketplaceGid | null
  shiphero?: MarketplaceGid | null
  admin?: {
    impersonate?: Username
  }
  extensions?: Extension[]
}

export type Request = RequestFn & {
  /**
   * Adds authentication headers.
   *
   * An authenticated client in a server component enables dynamic rendering.
   */
  auth: Request

  /** Uses the legacy Django endpoint. */
  django: Request

  /** Uses the MAPI endpoint. */
  mapi: (marketplaceId?: MarketplaceGid) => Request

  /**
   * Uses the admin-api (AAPI) endpoint.
   *
   * Browser calls cannot use this endpoint: AAPI rejects requests without a client API key, and `getClientHeaders` does
   * not add a client API key in the browser.
   */
  aapi: (marketplaceId?: MarketplaceGid) => Request

  /** Uses the ShipHero sync worker endpoint. */
  shiphero: (marketplaceId?: MarketplaceGid) => Request

  /** Enables super-admin mode. */
  admin: (_: IAmDangerouslyBypassingAuthzBecause, impersonate?: Username) => Request

  with: (extension: Extension) => Request
}

function createRequest(config: RequestConfig): Request {
  function baseRequest<T, V extends UnknownRecord>(document: TadaDocumentNode<T, V>, variables: V): Promise<T> {
    let client_ = client.with(graph)
    if (config.django) {
      client_ = client_.with(django)
    }
    // Use `!== undefined`, not truthiness. `null` means an unscoped MAPI request: the endpoint is applied without a
    // marketplace header. A falsy check would silently leave these requests on the Graph endpoint.
    if (config.mapi !== undefined) {
      client_ = client_.with(mapi(config.mapi))
    }
    // Use `!== undefined`, not truthiness. `null` means an unscoped AAPI request: the endpoint is applied without a
    // marketplace header. A falsy check would silently leave these requests on the Graph endpoint.
    if (config.aapi !== undefined) {
      client_ = client_.with(aapi(config.aapi))
    }
    // Use `!== undefined`, not truthiness. `null` means an unscoped ShipHero request: the endpoint is applied without a
    // marketplace header. A falsy check would silently leave these requests on the Graph endpoint.
    if (config.shiphero !== undefined) {
      client_ = client_.with(shiphero(config.shiphero))
    }
    if (config.auth) {
      client_ = client_.with(auth)
    }
    if (config.admin) {
      client_ = client_.with(globalConfig.extensions.admin(config.admin.impersonate))
    }

    for (const extension of config.extensions ?? []) {
      client_ = client_.with(extension)
    }

    return client_.request<T>(document, variables)
  }

  // oxlint-disable-next-line prefer-const, init-declarations
  let request: Request

  request = Object.assign(baseRequest, {
    get auth(): Request {
      return config.auth ? request : createRequest({ ...config, auth: true })
    },
    get django(): Request {
      return config.django ? request : createRequest({ ...config, django: true })
    },
    get mapi(): (marketplaceId?: MarketplaceGid) => Request {
      // Normalize before comparing. The stored value for no marketplace is `null`, so a bare `.mapi()` on a fresh chain
      // must not short-circuit against the unset (`undefined`) configuration.
      return (marketplaceId?: MarketplaceGid) =>
        config.mapi === (marketplaceId ?? null) ? request : createRequest({ ...config, mapi: marketplaceId ?? null })
    },
    get aapi(): (marketplaceId?: MarketplaceGid) => Request {
      // Normalize before comparing. The stored value for no marketplace is `null`, so a bare `.aapi()` on a fresh chain
      // must not short-circuit against the unset (`undefined`) configuration.
      return (marketplaceId?: MarketplaceGid) =>
        config.aapi === (marketplaceId ?? null) ? request : createRequest({ ...config, aapi: marketplaceId ?? null })
    },
    get shiphero(): (marketplaceId?: MarketplaceGid) => Request {
      // Normalize before comparing. The stored value for no marketplace is `null`, so a bare `.shiphero()` on a fresh
      // chain must not short-circuit against the unset (`undefined`) configuration.
      return (marketplaceId?: MarketplaceGid) =>
        config.shiphero === (marketplaceId ?? null)
          ? request
          : createRequest({ ...config, shiphero: marketplaceId ?? null })
    },
    admin(_: IAmDangerouslyBypassingAuthzBecause, impersonate?: Username) {
      return config.admin ? request : createRequest({ ...config, admin: shake({ impersonate }) })
    },
    with(extension: Extension) {
      return createRequest({ ...config, extensions: [...(config.extensions ?? []), extension] })
    },
  })

  return request
}
export const request = createRequest({})

type IAmDangerouslyBypassingAuthzBecause = `I am dangerously bypassing authz because: ${string}`
